import { useState, useCallback } from 'react';
import apiService from '../services/api';

export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAds = useCallback(async () => {
    console.log('📡 useApi.fetchAds called');

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      console.log('🔑 useApi: Token exists?', !!token);

      const ads = await apiService.fetchAds();

      console.log(`✅ useApi: Returning ${ads.length} transformed ads`);

      if (ads.length > 0) {
        console.log('📋 useApi: Sample is_liked status:',
          ads.slice(0, 3).map(ad => ({ id: ad.id, title: ad.title, is_liked: ad.is_liked, like_count: ad.like_count }))
        );
      }

      return ads;

    } catch (err) {
      setError(err.message);
      console.error('❌ useApi: Error fetching ads:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    console.log('📡 useApi.fetchJobs called');

    setLoading(true);
    setError(null);

    try {
      const data = await apiService.fetchJobs();
      const jobsArray = Array.isArray(data) ? data : (data.results || []);

      if (jobsArray.length === 0) {
        console.log('⚠️ useApi: No jobs from backend, returning mock data');
        return [
          {
            id: 1,
            title: 'Software Engineer',
            company: 'Tech Innovations Inc.',
            salaryMin: 80000,
            salaryMax: 120000,
            salaryType: 'year',
            icon: '💼',
            iconBgColor: '#dbeafe',
          },
          {
            id: 2,
            title: 'Retail Store Manager',
            company: 'ShopEase Inc.',
            salaryMin: 50000,
            salaryMax: 70000,
            salaryType: 'year',
            icon: '🏪',
            iconBgColor: '#d1fae5',
          },
        ];
      }

      console.log(`✅ useApi: Returning ${jobsArray.length} jobs`);
      return jobsArray;

    } catch (err) {
      setError(err.message);
      console.error('❌ useApi: Error fetching jobs:', err);
      return [
        {
          id: 1,
          title: 'Software Engineer',
          company: 'Tech Innovations Inc.',
          salaryMin: 80000,
          salaryMax: 120000,
          salaryType: 'year',
          icon: '💼',
          iconBgColor: '#dbeafe',
        },
        {
          id: 2,
          title: 'Retail Store Manager',
          company: 'ShopEase Inc.',
          salaryMin: 50000,
          salaryMax: 70000,
          salaryType: 'year',
          icon: '🏪',
          iconBgColor: '#d1fae5',
        },
      ];
    } finally {
      setLoading(false);
    }
  }, []);

  // Update an ad
  const updateAd = useCallback(async (adId, data) => {
    console.log('📡 useApi.updateAd called', { adId, data });

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication required. Please log in.');
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api';
      
      const response = await fetch(`${API_BASE_URL}/ads/${adId}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to update ad (${response.status})`);
      }

      const updatedAd = await response.json();
      console.log('✅ useApi: Ad updated successfully', updatedAd);
      
      return updatedAd;

    } catch (err) {
      setError(err.message);
      console.error('❌ useApi: Error updating ad:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ UPDATED: Delete ad using server-side endpoint that auto-creates
  // the Re-Post promotion — avoids 403 Forbidden from /admin/promotions/
  // createRePostPromotion removed: server handles it atomically now.
  const deleteAd = useCallback(async (adId, adData = null) => {
    console.log('📡 useApi.deleteAd called', { adId, adData });

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      if (!token) {
        throw new Error('Authentication required. Please log in.');
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api';

      // ✅ Use /ads/<id>/delete/ — server checks plan, creates promo, deletes ad atomically
      const response = await fetch(`${API_BASE_URL}/ads/${adId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Failed to delete ad (${response.status})`);
      }

      // Server returns 200 + JSON: { success, message, promotion_created, promotion? }
      let result;
      if (response.status === 204) {
        result = { success: true, id: adId, message: 'Ad deleted successfully' };
      } else {
        result = await response.json();
      }

      console.log('✅ useApi: Ad deleted successfully', result);

      if (result.promotion_created) {
        console.log('🎁 Re-post promotion created by server:', result.promotion);
      }

      return result;

    } catch (err) {
      setError(err.message);
      console.error('❌ useApi: Error deleting ad:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    fetchAds,
    fetchJobs,
    updateAd,
    deleteAd,
  };
};

